import { createContext, useContext, useMemo } from "react";
import { createTailSpendClient } from "@repo/api-client";
import { jsx } from "react/jsx-runtime";
import { QueryClient, QueryClientProvider, skipToken, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
//#region src/client.ts
/**
* Build a typed openapi-fetch client. `getIdToken` is awaited per request so
* a refresh that lands between hooks is picked up automatically.
*
* We mutate the supplied `Request` in place rather than cloning. RN's
* whatwg-fetch-derived polyfill does not reliably forward the original body
* through `new Request(request, { headers })` when init lacks an explicit
* `body`, so cloning would silently strip POST/PUT/PATCH/DELETE payloads on
* mobile. openapi-fetch builds a fresh Request per call, so direct
* mutation is safe.
*/
function buildAuthenticatedClient(args) {
	return createTailSpendClient({
		baseUrl: args.baseUrl,
		fetch: async (request) => {
			const idToken = await args.getIdToken();
			request.headers.set("authorization", `Bearer ${idToken}`);
			const response = await fetch(request);
			if (response.status === 401) args.onUnauthorized?.();
			return response;
		}
	});
}
/**
* Anonymous client for the public-invite endpoints
* (`/groups/{groupId}/invite/...`) that are gated by the per-group
* `INVITE.Enabled` flag rather than a Bearer token.
*/
function buildAnonymousClient(args) {
	return createTailSpendClient({ baseUrl: args.baseUrl });
}
//#endregion
//#region src/ApiProvider.tsx
const ApiContext = createContext(null);
function ApiProvider({ baseUrl, getIdToken, onUnauthorized, children }) {
	const value = useMemo(() => ({
		baseUrl,
		getIdToken,
		authenticatedClient: buildAuthenticatedClient({
			baseUrl,
			getIdToken,
			onUnauthorized
		}),
		anonymousClient: buildAnonymousClient({ baseUrl })
	}), [
		baseUrl,
		getIdToken,
		onUnauthorized
	]);
	return /* @__PURE__ */ jsx(ApiContext.Provider, {
		value,
		children
	});
}
function useApi() {
	const ctx = useContext(ApiContext);
	if (!ctx) throw new Error("useApi must be used inside <ApiProvider>");
	return ctx.authenticatedClient;
}
function useAnonymousApi() {
	const ctx = useContext(ApiContext);
	if (!ctx) throw new Error("useAnonymousApi must be used inside <ApiProvider>");
	return ctx.anonymousClient;
}
//#endregion
//#region src/QueryProvider.tsx
/**
* Default app-wide React Query client. Tuned for thin REST endpoints: a
* 30-second stale window, no refetch on window focus (loud and pointless on
* native), and a single retry to swallow transient network blips.
*/
function QueryProvider({ children }) {
	return /* @__PURE__ */ jsx(QueryClientProvider, {
		client: useMemo(() => new QueryClient({ defaultOptions: { queries: {
			staleTime: 3e4,
			refetchOnWindowFocus: false,
			retry: 1
		} } }), []),
		children
	});
}
//#endregion
//#region src/draft.ts
function mapDraftToFormSeed(options) {
	const { draft, groupCurrency, participantIds, payerParticipantId, lowConfidenceThreshold = .6 } = options;
	const foodIndexes = (draft.items ?? []).flatMap((item, index) => item.category === "FOOD" && (item.cost ?? 0) > 0 ? [index] : []);
	const items = (draft.items ?? []).map((item) => ({
		label: item.label,
		cost: item.cost ?? 0,
		...item.discountScope === "FOOD" && (item.cost ?? 0) < 0 && foodIndexes.length > 0 ? {
			splitType: "ITEM_TOTALS",
			itemIndexes: foodIndexes
		} : item.split?.type === "ITEM_TOTALS" ? {
			splitType: "ITEM_TOTALS",
			...item.split.itemIndexes ? { itemIndexes: item.split.itemIndexes } : {}
		} : isServiceCharge(item.label) || item.discountScope === "ALL" ? { splitType: "ITEM_TOTALS" } : {}
	}));
	const confidence = draft.confidence ?? {};
	const lowConfidenceFields = [
		"label",
		"currency",
		"total"
	].filter((key) => {
		const c = confidence[key];
		return typeof c === "number" && c <= lowConfidenceThreshold;
	});
	return {
		label: draft.label ?? "",
		currency: draft.currency ?? groupCurrency,
		items,
		total: draft.total,
		reviewNotes: (draft.items ?? []).flatMap((item) => (item.cost ?? 0) < 0 ? [item.discountScope === "FOOD" && foodIndexes.length > 0 ? `${item.label}: linked to food items. Check the selected items; the exact printed discount is preserved.` : `${item.label}: check which purchases qualify and choose “By item totals” → “Specific items” if needed.`] : []),
		participantIds,
		payerParticipantId,
		lowConfidenceFields
	};
}
function isServiceCharge(label) {
	return /\bservice\s+charges?\b/i.test(label);
}
//#endregion
//#region src/initials.ts
/**
* Up-to-two-character avatar initials for a display name. Shared by web,
* desktop and mobile so one person's avatar is the same glyph everywhere —
* this used to be duplicated per stack, which is exactly how two clients drift
* into showing different initials for the same name.
*
* ## Why punctuation splits
*
* Display names arrive in two shapes. A name the user typed is spaced
* (`"Fraser Benjamin"`), but a name TailSpend seeded for them is the email
* local-part (`"fraser.benjamin"` — see the Cognito provisioning trigger). A
* whitespace-only split reads the first as two names and the second as one, so
* the *same person* rendered as `FB` in one place and `F` in another, and their
* avatar flipped between one and two letters the moment the name was edited or
* synced (issue #462).
*
* So `.`, `_` and `+` — which never appear inside a human name, but are the
* usual email-local-part separators — split like whitespace does.
*
* The hyphen deliberately does NOT split: it genuinely appears inside names, so
* `"Jean-Luc"` is one name (`JL` would be wrong) and `"Mary-Jane Watson"` still
* resolves to `MW` on the whitespace boundary.
*
* An `@` truncates to the local part, so a full email address passed by mistake
* can't contribute a letter from the domain.
*
* Returns an empty string for a missing or punctuation-only name so each caller
* can choose its own placeholder (an icon, a neutral circle) rather than being
* handed a literal "?".
*/
function getInitials(displayName) {
	if (!displayName) return "";
	const parts = (displayName.split("@")[0] ?? "").split(/[\s._+]+/).map((part) => part.trim()).filter(Boolean);
	if (parts.length === 0) return "";
	if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
	return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}
//#endregion
//#region src/hooks.ts
function unwrap(result) {
	if (result.error || !result.response.ok) throw result.error ?? /* @__PURE__ */ new Error(`Request failed (${result.response.status})`);
	return result.data;
}
function useMe() {
	const api = useApi();
	return useQuery({
		queryKey: ["users", "me"],
		queryFn: async () => unwrap(await api.GET("/users/me"))
	});
}
function useUpdateMe() {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.PATCH("/users/me", { body })),
		onSuccess: (user) => {
			qc.setQueryData(["users", "me"], user);
		}
	});
}
/**
* Deletes the authenticated caller's account (`DELETE /users/me`). On success
* every cached query is dropped — the account no longer exists — so the caller
* should follow up by clearing the auth session (sign out) and routing to the
* signed-out landing state.
*/
function useDeleteMe() {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async () => unwrap(await api.DELETE("/users/me")),
		onSuccess: () => {
			qc.clear();
		}
	});
}
function useUpdateSettings() {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.PATCH("/users/me/settings", { body })),
		onSuccess: (user) => {
			qc.setQueryData(["users", "me"], user);
			qc.invalidateQueries({ queryKey: ["groups", "mine"] });
		}
	});
}
function useUser(userId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"users",
			"public",
			userId
		],
		queryFn: userId ? async () => unwrap(await api.GET("/users/{userId}", { params: { path: { userId } } })) : skipToken
	});
}
/**
* Another user's profile view: who they are plus the groups you're both in
* (issue #452). The server intersects the group list with the caller's own
* memberships and carries nothing account-private — no email, no credits.
*/
function useUserProfile(userId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"users",
			"public",
			userId,
			"profile"
		],
		queryFn: userId ? async () => unwrap(await api.GET("/users/{userId}/profile", { params: { path: { userId } } })) : skipToken
	});
}
function useMyGroups() {
	const api = useApi();
	return useQuery({
		queryKey: ["groups", "mine"],
		queryFn: async () => unwrap(await api.GET("/users/me/groups"))
	});
}
function useGroup(groupId) {
	const api = useApi();
	return useQuery({
		queryKey: ["groups", groupId],
		queryFn: groupId ? async () => unwrap(await api.GET("/groups/{groupId}", { params: { path: { groupId } } })) : skipToken
	});
}
function useCreateGroup() {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.POST("/groups", { body })),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["groups"] });
		}
	});
}
function useUpdateGroup(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.PATCH("/groups/{groupId}", {
			params: { path: { groupId } },
			body
		})),
		onSuccess: (group) => {
			qc.setQueryData(["groups", groupId], group);
			qc.invalidateQueries({ queryKey: ["groups", "mine"] });
		}
	});
}
function useDeleteGroup() {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (groupId) => unwrap(await api.DELETE("/groups/{groupId}", { params: { path: { groupId } } })),
		onSuccess: (_data, groupId) => {
			qc.removeQueries({ queryKey: ["groups", groupId] });
			qc.invalidateQueries({ queryKey: ["groups", "mine"] });
		}
	});
}
function useParticipants(groupId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"groups",
			groupId,
			"participants"
		],
		queryFn: groupId ? async () => unwrap(await api.GET("/groups/{groupId}/participants", { params: { path: { groupId } } })) : skipToken
	});
}
function useAddParticipant(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.POST("/groups/{groupId}/participants", {
			params: { path: { groupId } },
			body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["groups", groupId] });
		}
	});
}
function useUpdateParticipant(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (args) => unwrap(await api.PATCH("/groups/{groupId}/participants/{participantId}", {
			params: { path: {
				groupId,
				participantId: args.participantId
			} },
			body: args.body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["groups", groupId] });
		}
	});
}
function useRemoveParticipant(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (participantId) => unwrap(await api.DELETE("/groups/{groupId}/participants/{participantId}", { params: { path: {
			groupId,
			participantId
		} } })),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["groups", groupId] });
		}
	});
}
/**
* Moves everything a participant is involved in onto another participant, then
* deletes the emptied one (issue #448) — the "added as a guest, later joined
* with their own account" cleanup.
*
* Invalidates the whole group subtree: the rewrite touches expenses,
* settlements, balances, and the roster, so nothing cached for this group can be
* assumed intact.
*/
function useTransferParticipant(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (input) => unwrap(await api.POST("/groups/{groupId}/participants/{participantId}/transfer", {
			params: { path: {
				groupId,
				participantId: input.participantId
			} },
			body: input.body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["groups", groupId] });
			qc.invalidateQueries({ queryKey: ["groups", "mine"] });
		}
	});
}
function useExpenses(groupId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"groups",
			groupId,
			"expenses"
		],
		queryFn: groupId ? async () => unwrap(await api.GET("/groups/{groupId}/expenses", { params: { path: { groupId } } })) : skipToken
	});
}
/**
* Cursor-paginated expenses for the full group expense list. Unlike
* {@link useExpenses} (a single first page, used for counts/summaries), this
* walks pages on demand via `fetchNextPage`, so the UI can offer a
* "Load more" control and eventually show every expense.
*/
function useInfiniteExpenses(groupId) {
	const api = useApi();
	return useInfiniteQuery({
		queryKey: [
			"groups",
			groupId,
			"expenses",
			"infinite"
		],
		queryFn: groupId ? async ({ pageParam }) => unwrap(await api.GET("/groups/{groupId}/expenses", { params: {
			path: { groupId },
			query: pageParam ? { cursor: pageParam } : {}
		} })) : skipToken,
		initialPageParam: void 0,
		getNextPageParam: (lastPage) => lastPage.nextCursor
	});
}
/**
* Fetch a single expense by id, including its comments.
*
* Reads the dedicated `GET /groups/{groupId}/expenses/{expenseId}` route,
* which returns the full expense partition (head + comments). The list
* endpoints only project expense heads (`comments: []`), so the detail view
* must use this route — otherwise freshly-posted comments never appear. A
* 404 surfaces as `null` for the "expense not found" UI.
*/
function useExpense(groupId, expenseId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"groups",
			groupId,
			"expenses",
			expenseId
		],
		queryFn: groupId && expenseId ? async () => {
			const res = await api.GET("/groups/{groupId}/expenses/{expenseId}", { params: { path: {
				groupId,
				expenseId
			} } });
			if (res.response.status === 404) return null;
			return unwrap(res);
		} : skipToken
	});
}
function useCreateExpense(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.POST("/groups/{groupId}/expenses", {
			params: { path: { groupId } },
			body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"expenses"
			] });
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"balances"
			] });
			qc.invalidateQueries({ queryKey: ["groups", "mine"] });
		}
	});
}
function useUpdateExpense(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (args) => unwrap(await api.PATCH("/groups/{groupId}/expenses/{expenseId}", {
			params: { path: {
				groupId,
				expenseId: args.expenseId
			} },
			body: args.body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"expenses"
			] });
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"balances"
			] });
			qc.invalidateQueries({ queryKey: ["groups", "mine"] });
		}
	});
}
function useDeleteExpense(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (expenseId) => unwrap(await api.DELETE("/groups/{groupId}/expenses/{expenseId}", { params: { path: {
			groupId,
			expenseId
		} } })),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"expenses"
			] });
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"balances"
			] });
			qc.invalidateQueries({ queryKey: ["groups", "mine"] });
		}
	});
}
/**
* The group's balance breakdown: each participant's net (positive = owed,
* negative = owing) plus the simplified who-owes-who transfer plan. Reflects
* expenses and settlements together.
*/
function useGroupBalances(groupId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"groups",
			groupId,
			"balances"
		],
		queryFn: groupId ? async () => unwrap(await api.GET("/groups/{groupId}/balances", { params: { path: { groupId } } })) : skipToken
	});
}
/** First page of a group's settlement history (newest first). */
function useSettlements(groupId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"groups",
			groupId,
			"settlements"
		],
		queryFn: groupId ? async () => unwrap(await api.GET("/groups/{groupId}/settlements", { params: { path: { groupId } } })) : skipToken
	});
}
/**
* Cursor-paginated settlement history for the full transaction list. Mirrors
* {@link useInfiniteExpenses}: walks pages on demand via `fetchNextPage`.
*/
function useInfiniteSettlements(groupId) {
	const api = useApi();
	return useInfiniteQuery({
		queryKey: [
			"groups",
			groupId,
			"settlements",
			"infinite"
		],
		queryFn: groupId ? async ({ pageParam }) => unwrap(await api.GET("/groups/{groupId}/settlements", { params: {
			path: { groupId },
			query: pageParam ? { cursor: pageParam } : {}
		} })) : skipToken,
		initialPageParam: void 0,
		getNextPageParam: (lastPage) => lastPage.nextCursor
	});
}
function useSettlement(groupId, settlementId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"groups",
			groupId,
			"settlements",
			settlementId
		],
		queryFn: groupId && settlementId ? async () => {
			const res = await api.GET("/groups/{groupId}/settlements/{settlementId}", { params: { path: {
				groupId,
				settlementId
			} } });
			if (res.response.status === 404) return null;
			return unwrap(res);
		} : skipToken
	});
}
/**
* Recording, editing, or deleting a settlement changes balances, so every
* settlement mutation invalidates the settlements list, the balance
* breakdown, and the dashboard's per-group balance (`["groups", "mine"]`).
*/
function invalidateSettlementQueries(qc, groupId) {
	qc.invalidateQueries({ queryKey: [
		"groups",
		groupId,
		"settlements"
	] });
	qc.invalidateQueries({ queryKey: [
		"groups",
		groupId,
		"balances"
	] });
	qc.invalidateQueries({ queryKey: ["groups", "mine"] });
}
function useCreateSettlement(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.POST("/groups/{groupId}/settlements", {
			params: { path: { groupId } },
			body
		})),
		onSuccess: () => invalidateSettlementQueries(qc, groupId)
	});
}
function useUpdateSettlement(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (args) => unwrap(await api.PATCH("/groups/{groupId}/settlements/{settlementId}", {
			params: { path: {
				groupId,
				settlementId: args.settlementId
			} },
			body: args.body
		})),
		onSuccess: () => invalidateSettlementQueries(qc, groupId)
	});
}
function useDeleteSettlement(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (settlementId) => unwrap(await api.DELETE("/groups/{groupId}/settlements/{settlementId}", { params: { path: {
			groupId,
			settlementId
		} } })),
		onSuccess: () => invalidateSettlementQueries(qc, groupId)
	});
}
function useRecurringExpenses(groupId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"groups",
			groupId,
			"recurring-expenses"
		],
		queryFn: groupId ? async () => unwrap(await api.GET("/groups/{groupId}/recurring-expenses", { params: { path: { groupId } } })) : skipToken
	});
}
function useRecurringExpense(groupId, recurringExpenseId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"groups",
			groupId,
			"recurring-expenses",
			recurringExpenseId
		],
		queryFn: groupId && recurringExpenseId ? async () => unwrap(await api.GET("/groups/{groupId}/recurring-expenses/{recurringExpenseId}", { params: { path: {
			groupId,
			recurringExpenseId
		} } })) : skipToken
	});
}
function useCreateRecurringExpense(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.POST("/groups/{groupId}/recurring-expenses", {
			params: { path: { groupId } },
			body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"recurring-expenses"
			] });
		}
	});
}
function useUpdateRecurringExpense(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (args) => unwrap(await api.PATCH("/groups/{groupId}/recurring-expenses/{recurringExpenseId}", {
			params: { path: {
				groupId,
				recurringExpenseId: args.recurringExpenseId
			} },
			body: args.body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"recurring-expenses"
			] });
		}
	});
}
function useDeleteRecurringExpense(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (recurringExpenseId) => unwrap(await api.DELETE("/groups/{groupId}/recurring-expenses/{recurringExpenseId}", { params: { path: {
			groupId,
			recurringExpenseId
		} } })),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"recurring-expenses"
			] });
		}
	});
}
function useAddComment(groupId, expenseId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.POST("/groups/{groupId}/expenses/{expenseId}/comments", {
			params: { path: {
				groupId,
				expenseId
			} },
			body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"expenses"
			] });
		}
	});
}
function useUpdateComment(groupId, expenseId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (args) => unwrap(await api.PUT("/groups/{groupId}/expenses/{expenseId}/comments/{commentId}", {
			params: { path: {
				groupId,
				expenseId,
				commentId: args.commentId
			} },
			body: args.body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"expenses"
			] });
		}
	});
}
function useDeleteComment(groupId, expenseId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (commentId) => unwrap(await api.DELETE("/groups/{groupId}/expenses/{expenseId}/comments/{commentId}", { params: { path: {
			groupId,
			expenseId,
			commentId
		} } })),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"expenses"
			] });
		}
	});
}
function usePublicGroupInvite(groupId) {
	const api = useAnonymousApi();
	return useQuery({
		queryKey: ["invite", groupId],
		queryFn: groupId ? async () => unwrap(await api.GET("/groups/{groupId}/invite", { params: { path: { groupId } } })) : skipToken
	});
}
function usePublicGroupInviteExpenses(groupId) {
	const api = useAnonymousApi();
	return useQuery({
		queryKey: [
			"invite",
			groupId,
			"expenses"
		],
		queryFn: groupId ? async () => unwrap(await api.GET("/groups/{groupId}/invite/expenses", { params: { path: { groupId } } })) : skipToken
	});
}
/**
* Cursor-paginated public expenses for the guest invite view. The
* unauthenticated counterpart to {@link useInfiniteExpenses}: walks pages on
* demand so guests can "Load more" and eventually browse every expense in the
* group, not just the first page returned by {@link usePublicGroupInviteExpenses}.
*/
function useInfinitePublicGroupInviteExpenses(groupId) {
	const api = useAnonymousApi();
	return useInfiniteQuery({
		queryKey: [
			"invite",
			groupId,
			"expenses",
			"infinite"
		],
		queryFn: groupId ? async ({ pageParam }) => unwrap(await api.GET("/groups/{groupId}/invite/expenses", { params: {
			path: { groupId },
			query: pageParam ? { cursor: pageParam } : {}
		} })) : skipToken,
		initialPageParam: void 0,
		getNextPageParam: (lastPage) => lastPage.nextCursor
	});
}
function usePublicGroupInviteExpense(groupId, expenseId) {
	const api = useAnonymousApi();
	return useQuery({
		queryKey: [
			"invite",
			groupId,
			"expenses",
			expenseId
		],
		queryFn: groupId && expenseId ? async () => unwrap(await api.GET("/groups/{groupId}/invite/expenses/{expenseId}", { params: { path: {
			groupId,
			expenseId
		} } })) : skipToken
	});
}
function useSetInviteEnabled(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.PATCH("/groups/{groupId}/invite", {
			params: { path: { groupId } },
			body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["invite", groupId] });
		}
	});
}
function useSubmitJoinRequest(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.POST("/groups/{groupId}/join-requests", {
			params: { path: { groupId } },
			body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["join-requests", "mine"] });
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"join-requests"
			] });
		}
	});
}
function useJoinRequests(groupId, options) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"groups",
			groupId,
			"join-requests"
		],
		queryFn: groupId && options?.enabled !== false ? async () => unwrap(await api.GET("/groups/{groupId}/join-requests", { params: { path: { groupId } } })) : skipToken
	});
}
function useApproveJoinRequest(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (args) => unwrap(await api.POST("/groups/{groupId}/join-requests/{requestId}/approve", {
			params: { path: {
				groupId,
				requestId: args.requestId
			} },
			body: args.body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"join-requests"
			] });
			qc.invalidateQueries({ queryKey: ["groups", groupId] });
			qc.invalidateQueries({ queryKey: ["groups", "mine"] });
		}
	});
}
function useDenyJoinRequest(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (requestId) => unwrap(await api.POST("/groups/{groupId}/join-requests/{requestId}/deny", { params: { path: {
			groupId,
			requestId
		} } })),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"groups",
				groupId,
				"join-requests"
			] });
		}
	});
}
function useCancelMyJoinRequest(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async () => unwrap(await api.DELETE("/groups/{groupId}/join-requests/mine", { params: { path: { groupId } } })),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["join-requests", "mine"] });
		}
	});
}
function useMyJoinRequests(options) {
	const api = useApi();
	return useQuery({
		queryKey: ["join-requests", "mine"],
		queryFn: options?.enabled === false ? skipToken : async () => unwrap(await api.GET("/users/me/join-requests"))
	});
}
function useMyCredits() {
	const api = useApi();
	return useQuery({
		queryKey: ["credits", "me"],
		queryFn: async () => unwrap(await api.GET("/users/me/credits"))
	});
}
function useCreateUploadTicket() {
	const api = useApi();
	return useMutation({ mutationFn: async (body) => unwrap(await api.POST("/scans/uploads", { body })) });
}
function useCreateScanJob(groupId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.POST("/groups/{groupId}/scan-jobs", {
			params: { path: { groupId } },
			body
		})),
		onSuccess: (job) => {
			qc.setQueryData([
				"scan-jobs",
				groupId,
				job.id
			], job);
		},
		onSettled: () => {
			qc.invalidateQueries({ queryKey: ["credits", "me"] });
		}
	});
}
function useScanJob(groupId, scanJobId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"scan-jobs",
			groupId,
			scanJobId
		],
		queryFn: scanJobId ? async () => unwrap(await api.GET("/groups/{groupId}/scan-jobs/{scanJobId}", { params: { path: {
			groupId,
			scanJobId
		} } })) : skipToken,
		refetchInterval: (query) => {
			const stage = query.state.data?.stage;
			return stage === "COMPLETE" || stage === "FAILED" ? false : 2e3;
		},
		refetchIntervalInBackground: true,
		staleTime: 0
	});
}
/**
* Page size the admin console asks for: the API's cap, deliberately.
*
* Two things shrink a page of users *after* it is read, so asking for fewer
* would surface far fewer than it looks like (issue #465):
*
* 1. The underlying DynamoDB `Scan` applies its `Limit` to items **evaluated**,
*    before the `SK = PROFILE` filter — and a user's partition also holds their
*    balance, credit ledger and idempotency markers, so most scanned rows are
*    not profiles.
* 2. The service then drops integration-test accounts and applies the `q`
*    substring match (see `AdminService.listUsers`).
*
* A short page is therefore normal and does NOT mean the end of the list — the
* cursor is what says that, which is why the page keeps paging on `nextCursor`.
*/
const ADMIN_USERS_PAGE_SIZE = 200;
function useAdminUsers(q, options) {
	const api = useApi();
	const query = q?.trim() ? q.trim() : void 0;
	const includeTestAccounts = options?.includeTestAccounts ?? false;
	return useInfiniteQuery({
		queryKey: [
			"admin",
			"users",
			query ?? "",
			includeTestAccounts
		],
		queryFn: async ({ pageParam }) => unwrap(await api.GET("/admin/users", { params: { query: {
			limit: ADMIN_USERS_PAGE_SIZE,
			...pageParam ? { cursor: pageParam } : {},
			...query ? { q: query } : {},
			...includeTestAccounts ? { includeTestAccounts: "true" } : {}
		} } })),
		initialPageParam: void 0,
		getNextPageParam: (last) => last.nextCursor
	});
}
function useAdminUser(userId) {
	const api = useApi();
	return useQuery({
		queryKey: [
			"admin",
			"users",
			"detail",
			userId
		],
		queryFn: userId ? async () => unwrap(await api.GET("/admin/users/{userId}", { params: { path: { userId } } })) : skipToken
	});
}
function useAdminAdjustCredits(userId) {
	const api = useApi();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => unwrap(await api.POST("/admin/users/{userId}/credits", {
			params: { path: { userId } },
			body
		})),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [
				"admin",
				"users",
				"detail",
				userId
			] });
		}
	});
}
//#endregion
export { ApiProvider, QueryProvider, buildAnonymousClient, buildAuthenticatedClient, getInitials, mapDraftToFormSeed, useAddComment, useAddParticipant, useAdminAdjustCredits, useAdminUser, useAdminUsers, useAnonymousApi, useApi, useApproveJoinRequest, useCancelMyJoinRequest, useCreateExpense, useCreateGroup, useCreateRecurringExpense, useCreateScanJob, useCreateSettlement, useCreateUploadTicket, useDeleteComment, useDeleteExpense, useDeleteGroup, useDeleteMe, useDeleteRecurringExpense, useDeleteSettlement, useDenyJoinRequest, useExpense, useExpenses, useGroup, useGroupBalances, useInfiniteExpenses, useInfinitePublicGroupInviteExpenses, useInfiniteSettlements, useJoinRequests, useMe, useMyCredits, useMyGroups, useMyJoinRequests, useParticipants, usePublicGroupInvite, usePublicGroupInviteExpense, usePublicGroupInviteExpenses, useRecurringExpense, useRecurringExpenses, useRemoveParticipant, useScanJob, useSetInviteEnabled, useSettlement, useSettlements, useSubmitJoinRequest, useTransferParticipant, useUpdateComment, useUpdateExpense, useUpdateGroup, useUpdateMe, useUpdateParticipant, useUpdateRecurringExpense, useUpdateSettings, useUpdateSettlement, useUser, useUserProfile };

//# sourceMappingURL=index.js.map